import { type Infer, v } from 'convex/values'

import { MOVE_IDS, isMoveId, moveValidator } from './exerciseMoves'
import { roundAmount } from './habitMeasure'
import { plateStep } from './levelUp'

// Workout session plans on a habit: a few named sessions of items (sets × reps,
// timed, distance) that rotate one per finished session. Pure helpers shared by
// sessionPlans.ts, the plan generator and the clients.

export const MAX_SESSIONS = 6
export const MAX_ITEMS = 15
export const MAX_NAME = 60
export const MAX_CUE = 100
export const MAX_UNIT = 8
export const MAX_DISMISSED = 40
// Done sessions read back for progression, PRs and the coach line.
export const HISTORY = 12

export const itemKindValidator = v.union(
  v.literal('sets'),
  v.literal('timed'),
  v.literal('distance'),
)
export type ItemKind = Infer<typeof itemKindValidator>

export const blockValidator = v.union(v.literal('warmup'), v.literal('cooldown'))
export type Block = Infer<typeof blockValidator>

export const planItemValidator = v.object({
  id: v.string(),
  name: v.string(),
  kind: itemKindValidator,
  sets: v.number(),
  reps: v.optional(v.number()),
  weight: v.optional(v.number()),
  seconds: v.optional(v.number()),
  distance: v.optional(v.number()),
  // kg / lb for sets, km / mi / m for distance.
  unit: v.optional(v.string()),
  cue: v.optional(v.string()),
  // Figure shown on the exercise card (lib/exerciseMoves); inferred from the name if absent.
  move: v.optional(moveValidator),
  // Timed holds done on each side: the countdown runs left, then right.
  eachSide: v.optional(v.boolean()),
  // Rest after each set, in seconds (restSeconds gives the default).
  rest: v.optional(v.number()),
  // Warm-up items run first and cool-down last; either can be skipped per session.
  block: v.optional(blockValidator),
})
export type PlanItem = Infer<typeof planItemValidator>

export const planSessionValidator = v.object({
  id: v.string(),
  name: v.string(),
  items: v.array(planItemValidator),
})
export type PlanSession = Infer<typeof planSessionValidator>

export const planProfileValidator = v.object({
  level: v.optional(
    v.union(v.literal('beginner'), v.literal('intermediate'), v.literal('advanced')),
  ),
  equipment: v.optional(v.union(v.literal('none'), v.literal('dumbbells'), v.literal('gym'))),
  minutes: v.optional(v.number()),
  // Yoga / stretching flows ask for a focus instead of equipment.
  focus: v.optional(v.union(v.literal('flexibility'), v.literal('strength'), v.literal('relax'))),
})
export type PlanProfile = Infer<typeof planProfileValidator>

export const setLogValidator = v.object({
  done: v.boolean(),
  reps: v.optional(v.number()),
  weight: v.optional(v.number()),
  seconds: v.optional(v.number()),
  distance: v.optional(v.number()),
})
export type SetLog = Infer<typeof setLogValidator>

export const itemLogValidator = v.object({
  itemId: v.string(),
  name: v.string(),
  kind: itemKindValidator,
  unit: v.optional(v.string()),
  move: v.optional(moveValidator),
  eachSide: v.optional(v.boolean()),
  rest: v.optional(v.number()),
  block: v.optional(blockValidator),
  // The plan's target when the session started; sets hold what was actually done.
  target: v.object({
    reps: v.optional(v.number()),
    weight: v.optional(v.number()),
    seconds: v.optional(v.number()),
    distance: v.optional(v.number()),
  }),
  sets: v.array(setLogValidator),
})
export type ItemLog = Infer<typeof itemLogValidator>

// Denormalised on the habit node for row meta lines ("Push day · 5 exercises").
export const planSummaryValidator = v.object({ name: v.string(), items: v.number() })

const clampNum = (n: number | undefined, min: number, max: number) =>
  n === undefined || !Number.isFinite(n) ? undefined : roundAmount(Math.min(max, Math.max(min, n)))

const clip = (s: string | undefined, max: number) =>
  (s ?? '').replace(/\s+/g, ' ').trim().slice(0, max)

export const newId = () => Math.random().toString(36).slice(2, 10)

export function cleanItem(raw: PlanItem): PlanItem | null {
  const name = clip(raw.name, MAX_NAME)
  if (!name) return null
  const kind = raw.kind
  const sets = Math.round(clampNum(raw.sets, 1, 10) ?? 1)
  const cue = clip(raw.cue, MAX_CUE)
  const unit = clip(raw.unit, MAX_UNIT).toLowerCase()
  const item: PlanItem = { id: clip(raw.id, 20) || newId(), name, kind, sets }
  if (cue) item.cue = cue
  if (isMoveId(raw.move)) item.move = raw.move
  const rest = clampNum(raw.rest, 0, 600)
  if (rest !== undefined) item.rest = Math.round(rest)
  if (raw.block === 'warmup' || raw.block === 'cooldown') item.block = raw.block
  if (kind === 'sets') {
    item.reps = Math.round(clampNum(raw.reps, 1, 200) ?? 10)
    const weight = clampNum(raw.weight, 0, 1000)
    if (weight) {
      item.weight = weight
      item.unit = unit === 'lb' || unit === 'lbs' ? 'lb' : 'kg'
    }
  } else if (kind === 'timed') {
    item.seconds = Math.round(clampNum(raw.seconds, 5, 4 * 3600) ?? 60)
    if (raw.eachSide) item.eachSide = true
  } else {
    item.distance = clampNum(raw.distance, 0.01, 1000) ?? 1
    item.unit = unit === 'mi' || unit === 'm' ? unit : 'km'
  }
  return item
}

export function cleanSessions(raw: PlanSession[]): PlanSession[] {
  const ids = new Set<string>()
  const out: PlanSession[] = []
  for (const s of raw.slice(0, MAX_SESSIONS)) {
    const items: PlanItem[] = []
    for (const it of s.items) {
      const c = cleanItem(it)
      if (!c) continue
      if (ids.has(c.id)) c.id = newId()
      ids.add(c.id)
      items.push(c)
      if (items.length >= MAX_ITEMS) break
    }
    if (!items.length) continue
    const rank = (i: PlanItem) => (i.block === 'warmup' ? 0 : i.block === 'cooldown' ? 2 : 1)
    items.sort((a, b) => rank(a) - rank(b))
    let id = clip(s.id, 20) || newId()
    if (ids.has(id)) id = newId()
    ids.add(id)
    out.push({ id, name: clip(s.name, MAX_NAME), items })
  }
  return out
}

export const nextIndex = (sessions: readonly PlanSession[], next: number) =>
  sessions.length ? ((next % sessions.length) + sessions.length) % sessions.length : 0

export const summaryOf = (sessions: readonly PlanSession[], next: number) => {
  const s = sessions[nextIndex(sessions, next)]
  return s ? { name: s.name, items: s.items.length } : undefined
}

export function itemLogFrom(item: PlanItem): ItemLog {
  const target = {
    ...(item.reps !== undefined && { reps: item.reps }),
    ...(item.weight !== undefined && { weight: item.weight }),
    ...(item.seconds !== undefined && { seconds: item.seconds }),
    ...(item.distance !== undefined && { distance: item.distance }),
  }
  return {
    itemId: item.id,
    name: item.name,
    kind: item.kind,
    ...(item.unit && { unit: item.unit }),
    ...(item.move && { move: item.move }),
    ...(item.eachSide && { eachSide: true }),
    ...(item.rest !== undefined && { rest: item.rest }),
    ...(item.block && { block: item.block }),
    target,
    sets: Array.from({ length: item.sets }, () => ({ done: false, ...target })),
  }
}

export const itemDone = (l: ItemLog) => l.sets.every((s) => s.done)

// Rest between sets: the item's own, else 90 s after weighted sets and 60 s otherwise.
export const restSeconds = (i: { rest?: number; weight?: number; target?: { weight?: number } }) =>
  i.rest ?? (i.weight || i.target?.weight ? 90 : 60)

export type LastTime = {
  kind: ItemKind
  sets: number
  reps?: number
  weight?: number
  seconds?: number
  distance?: number
  unit?: string
  date: string
}

// What was done for each item the last time it was logged (newest first in
// `done`; unlogged ticks have no sets and are skipped): done sets and the top set.
export function lastTimes(done: readonly { date: string; items: ItemLog[]; unlogged?: boolean }[]) {
  const out: Record<string, LastTime> = {}
  for (const d of done) {
    if (d.unlogged) continue
    for (const l of d.items) {
      if (out[l.itemId]) continue
      const sets = l.sets.filter((x) => x.done)
      if (!sets.length) continue
      const top = sets.reduce((a, b) =>
        (b.weight ?? 0) > (a.weight ?? 0) ||
        ((b.weight ?? 0) === (a.weight ?? 0) &&
          (b.reps ?? b.seconds ?? b.distance ?? 0) > (a.reps ?? a.seconds ?? a.distance ?? 0))
          ? b
          : a,
      )
      out[l.itemId] = {
        kind: l.kind,
        sets: sets.length,
        date: d.date,
        ...(top.reps !== undefined && { reps: top.reps }),
        ...(top.weight !== undefined && { weight: top.weight }),
        ...(top.seconds !== undefined && { seconds: top.seconds }),
        ...(top.distance !== undefined && { distance: top.distance }),
        ...(l.unit && { unit: l.unit }),
      }
    }
  }
  return out
}

export type Balance = {
  kind: 'slipping' | 'dominant'
  sessionId: string
  name: string
  // The session that's taken over (dominant only).
  over?: string
}

const dayNum = (d: string) => Math.floor(Date.parse(`${d}T00:00:00Z`) / 86_400_000)

export const balanceKey = (sessionId: string, today: string) =>
  `balance:${sessionId}:${Math.floor(dayNum(today) / 7)}`

// A rotation session that keeps slipping: in each of the last two weeks it was
// ticked without being run, or not done while the others were. Otherwise, one
// session that took 75%+ of 4+ logged sessions in two weeks while another had none.
export function balanceNudge(
  sessions: readonly PlanSession[],
  recent: readonly { date: string; sessionId: string; unlogged?: boolean }[],
  today: string,
  dismissed: readonly string[] = [],
): Balance | null {
  if (sessions.length < 2) return null
  const t = dayNum(today)
  const week = (d: string) => {
    const ago = t - dayNum(d)
    return ago >= 1 && ago <= 7 ? 0 : ago >= 8 && ago <= 14 ? 1 : -1
  }
  const rows = recent.filter((r) => week(r.date) >= 0)
  const open = (s: PlanSession) => !dismissed.includes(balanceKey(s.id, today))
  const logged = (id: string, w: number) =>
    rows.some((r) => r.sessionId === id && !r.unlogged && week(r.date) === w)
  for (const s of sessions) {
    if (!open(s)) continue
    const slipped = [0, 1].every(
      (w) =>
        !logged(s.id, w) &&
        rows.some(
          (r) =>
            week(r.date) === w &&
            ((r.sessionId === s.id && r.unlogged) || (r.sessionId !== s.id && !r.unlogged)),
        ),
    )
    if (slipped) return { kind: 'slipping', sessionId: s.id, name: s.name }
  }
  const counts = new Map(sessions.map((s) => [s.id, 0]))
  for (const r of rows)
    if (!r.unlogged && counts.has(r.sessionId))
      counts.set(r.sessionId, counts.get(r.sessionId)! + 1)
  const total = [...counts.values()].reduce((a, b) => a + b, 0)
  const [topId, top] = [...counts].sort((a, b) => b[1] - a[1])[0]
  const neglected = sessions.find((s) => counts.get(s.id) === 0 && open(s))
  if (total >= 4 && top / total >= 0.75 && neglected)
    return {
      kind: 'dominant',
      sessionId: neglected.id,
      name: neglected.name,
      over: sessions.find((s) => s.id === topId)?.name,
    }
  return null
}

export const balanceCoachText = (b: Balance) =>
  b.kind === 'slipping'
    ? `${b.name || 'a session'} keeps slipping (skipped 2 weeks running)`
    : `${b.over ?? 'one session'} has taken over; ${b.name || 'another session'} hasn't been done in 2 weeks`

// Every set done at or above the item's current plan target.
export function isClean(l: ItemLog, item: PlanItem): boolean {
  if (l.sets.length < item.sets || !itemDone(l)) return false
  return l.sets.every(
    (s) =>
      (item.reps === undefined || (s.reps ?? 0) >= item.reps) &&
      (item.weight === undefined || (s.weight ?? 0) >= item.weight) &&
      (item.seconds === undefined || (s.seconds ?? 0) >= item.seconds) &&
      (item.distance === undefined || (s.distance ?? 0) >= item.distance),
  )
}

export type Progression = {
  itemId: string
  name: string
  field: 'weight' | 'reps' | 'seconds' | 'distance'
  from: number
  to: number
  unit?: string
}

export const progressionKey = (p: Pick<Progression, 'itemId' | 'field' | 'from'>) =>
  `${p.itemId}:${p.field}:${p.from}`

// The next step for an item: plate-sized for weights (as number-habit level-ups),
// +1 rep, +5 s on short holds (+1 min on longer timed work), ~10% on distance.
export function progressionStep(item: PlanItem): Omit<Progression, 'name'> | null {
  if (item.kind === 'sets') {
    if (item.weight) {
      const step = plateStep(item.unit ?? 'kg', item.weight) ?? 2.5
      return {
        itemId: item.id,
        field: 'weight',
        from: item.weight,
        to: roundAmount(item.weight + step),
        unit: item.unit,
      }
    }
    if (item.reps === undefined || item.reps >= 200) return null
    return { itemId: item.id, field: 'reps', from: item.reps, to: item.reps + 1 }
  }
  if (item.kind === 'timed') {
    if (item.seconds === undefined) return null
    const step = item.seconds < 120 ? 5 : 60
    return { itemId: item.id, field: 'seconds', from: item.seconds, to: item.seconds + step }
  }
  if (item.distance === undefined) return null
  const step = Math.max(0.1, Math.round(item.distance) / 10)
  return {
    itemId: item.id,
    field: 'distance',
    from: item.distance,
    to: roundAmount(item.distance + step),
    unit: item.unit,
  }
}

// Two clean sessions in a row for an item (newest first in `done`) suggest a step up.
export function progressionsFor(
  session: PlanSession,
  done: readonly { items: ItemLog[] }[],
  dismissed: readonly string[],
): Progression[] {
  const out: Progression[] = []
  for (const item of session.items) {
    const logs = done
      .map((d) => d.items.find((l) => l.itemId === item.id))
      .filter((l): l is ItemLog => !!l)
      .slice(0, 2)
    if (logs.length < 2 || !logs.every((l) => isClean(l, item))) continue
    const step = progressionStep(item)
    if (!step || dismissed.includes(progressionKey(step))) continue
    out.push({ ...step, name: item.name })
  }
  return out
}

const bestOf = (l: ItemLog) => {
  const done = l.sets.filter((s) => s.done)
  if (!done.length) return 0
  if (l.kind === 'sets') return Math.max(...done.map((s) => s.weight ?? 0))
  if (l.kind === 'timed') return Math.max(...done.map((s) => s.seconds ?? 0))
  return Math.max(...done.map((s) => s.distance ?? 0))
}

// Items beating every earlier logged best (weight for lifts, hold/time, distance).
export function personalRecords(
  items: readonly ItemLog[],
  earlier: readonly { items: ItemLog[] }[],
) {
  const prs: string[] = []
  for (const l of items) {
    const best = bestOf(l)
    if (!best) continue
    const prev = earlier
      .map((d) => d.items.find((x) => x.itemId === l.itemId))
      .filter((x): x is ItemLog => !!x)
    if (!prev.length) continue
    if (best > Math.max(...prev.map(bestOf)))
      prs.push(`${l.name} ${targetText(l.kind, best, l.unit)}`)
  }
  return prs
}

export function targetText(kind: ItemKind, n: number, unit?: string) {
  if (kind === 'sets') return `${n} ${unit ?? 'kg'}`
  if (kind === 'timed') return n >= 60 && n % 60 === 0 ? `${n / 60} min` : `${n} s`
  return `${n} ${unit ?? 'km'}`
}

export function itemText(i: PlanItem) {
  if (i.kind === 'sets')
    return `${i.name} ${i.sets}×${i.reps ?? 0}${i.weight ? ` @ ${i.weight} ${i.unit ?? 'kg'}` : ''}`
  if (i.kind === 'timed')
    return `${i.name} ${i.sets > 1 ? `${i.sets}×` : ''}${targetText('timed', i.seconds ?? 0)}`
  return `${i.name} ${i.sets > 1 ? `${i.sets}×` : ''}${targetText('distance', i.distance ?? 0, i.unit)}`
}

// One compact coach line per planned habit.
export function coachLine(input: {
  title: string
  sessions: readonly PlanSession[]
  next: number
  recent: readonly {
    date: string
    sessionName: string
    items: ItemLog[]
    prs?: string[]
    unlogged?: boolean
  }[]
  balance?: Balance | null
}): string {
  const { sessions, recent } = input
  const names = sessions.map((s) => s.name || 'session')
  const nextS = sessions[nextIndex(sessions, input.next)]
  const parts = [
    `${input.title}: ${sessions.length > 1 ? `rotation ${names.join('/')}, next ${nextS?.name}` : `${nextS?.items.length ?? 0} items`}`,
  ]
  const last = recent[0]
  if (last?.unlogged)
    parts.push(`last ${last.sessionName || 'session'} ${last.date}: ticked, not logged`)
  else if (last) {
    const skipped = last.items.filter((l) => !itemDone(l)).map((l) => l.name)
    const done = last.items.length - skipped.length
    parts.push(
      `last ${last.sessionName || 'session'} ${last.date}: ${done}/${last.items.length} done${skipped.length ? `, skipped ${skipped.join(', ')}` : ''}${last.prs?.length ? `, PR ${last.prs.join(', ')}` : ''}`,
    )
  }
  const counts = new Map<string, number>()
  for (const r of recent)
    for (const l of r.items) if (!itemDone(l)) counts.set(l.name, (counts.get(l.name) ?? 0) + 1)
  const often = [...counts].filter(([, n]) => n >= 2).map(([name, n]) => `${name} ${n}×`)
  if (often.length) parts.push(`often skipped lately: ${often.slice(0, 3).join(', ')}`)
  if (input.balance) parts.push(balanceCoachText(input.balance))
  return parts.join('; ')
}

export const sessionPlanCoachNote = (lines: string) =>
  lines
    ? `Workout session plans (habit: rotation, last session, skips): ${lines}. If it fits, mention a pattern (e.g. a session or exercise they keep skipping) once, kindly; no medical advice.`
    : ''

// Plan generation prompt + JSON schema (strict: every field present, nulls allowed).
export const PLAN_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['sessions'],
  properties: {
    sessions: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['name', 'items'],
        properties: {
          name: { type: 'string' },
          items: {
            type: 'array',
            items: {
              type: 'object',
              additionalProperties: false,
              required: [
                'name',
                'kind',
                'sets',
                'reps',
                'weight',
                'seconds',
                'distance',
                'unit',
                'cue',
                'move',
                'eachSide',
                'rest',
                'block',
              ],
              properties: {
                name: { type: 'string' },
                kind: { type: 'string', enum: ['sets', 'timed', 'distance'] },
                sets: { type: 'number' },
                reps: { type: ['number', 'null'] },
                weight: { type: ['number', 'null'] },
                seconds: { type: ['number', 'null'] },
                distance: { type: ['number', 'null'] },
                unit: { type: ['string', 'null'] },
                cue: { type: 'string' },
                move: { type: 'string', enum: MOVE_IDS },
                eachSide: { type: ['boolean', 'null'] },
                rest: { type: ['number', 'null'] },
                block: { type: 'string', enum: ['warmup', 'main', 'cooldown'] },
              },
            },
          },
        },
      },
    },
  },
} as const

export type GeneratedPlan = {
  sessions: Array<{
    name: string
    items: Array<{
      name: string
      kind: string
      sets: number
      reps: number | null
      weight: number | null
      seconds: number | null
      distance: number | null
      unit: string | null
      cue: string
      move?: string | null
      eachSide?: boolean | null
      rest?: number | null
      block?: string | null
    }>
  }>
}

export function fromGenerated(g: GeneratedPlan): PlanSession[] {
  const kinds = new Set(['sets', 'timed', 'distance'])
  return cleanSessions(
    (g.sessions ?? []).map((s) => ({
      id: newId(),
      name: s.name ?? '',
      items: (s.items ?? [])
        .filter((i) => kinds.has(i.kind))
        .map((i) => ({
          id: newId(),
          name: i.name,
          kind: i.kind as ItemKind,
          sets: i.sets,
          reps: i.reps ?? undefined,
          weight: i.weight ?? undefined,
          seconds: i.seconds ?? undefined,
          distance: i.distance ?? undefined,
          unit: i.unit ?? undefined,
          cue: i.cue,
          ...(isMoveId(i.move) && i.move !== 'generic' && { move: i.move }),
          ...(i.eachSide && { eachSide: true }),
          ...(typeof i.rest === 'number' && { rest: i.rest }),
          ...((i.block === 'warmup' || i.block === 'cooldown') && { block: i.block }),
        })),
    })),
  )
}

export function planPrompt(input: {
  title: string
  description?: string
  level: string
  equipment: string
  minutes: number
  daysPerWeek?: number
  obstacle?: string
  language: string
  weightUnit: 'kg' | 'lb'
  // Yoga, stretching or mobility habit: a flow of held poses.
  flow?: boolean
  focus?: string
  // A running habit: warm-up and cool-down both.
  run?: boolean
}) {
  const head = [
    input.flow
      ? `Write a simple yoga / stretching flow for the habit "${input.title}".`
      : `Write a simple workout session plan for the habit "${input.title}".`,
    input.description ? `Habit note: ${input.description.slice(0, 300)}` : '',
    input.flow
      ? `Experience: ${input.level}. Focus: ${input.focus ?? 'flexibility'}. Time per session: about ${input.minutes} minutes.`
      : `Level: ${input.level}. Equipment: ${input.equipment === 'none' ? 'bodyweight only' : input.equipment === 'dumbbells' ? 'a pair of dumbbells at home' : 'a full gym'}. Time per session: about ${input.minutes} minutes.`,
    input.daysPerWeek ? `They train about ${input.daysPerWeek} days a week.` : '',
    input.obstacle
      ? `What has stopped them before: ${input.obstacle.slice(0, 200)}. Keep it realistic for that.`
      : '',
  ]
  const rules = input.flow
    ? [
        `Rules: usually 1 session (a flow); 2 only if a morning / evening split clearly fits. 6-12 poses per session in a sensible order (warm, standing, floor, rest), fitting the time.`,
        `Each pose: kind "timed", sets 1, seconds = hold time (20-90; longer for restful poses), eachSide true for one-sided poses (warrior, triangle, tree, low lunge) and false otherwise. reps, weight, distance, unit null.`,
        `move: the figure that fits best: downdog, cobra (also upward dog), child, catcow, warrior1, warrior2, tree, triangle, chair, bridgepose, forwardfold, lowlunge, mountain, stretch, or pose for anything else.`,
      ]
    : [
        `Rules: 1 session if the habit is a single routine (e.g. a daily run or plank); 2-3 named sessions that rotate (e.g. "Push day", "Pull day", "Legs day", or "Full body A/B") for strength training 3+ days a week. 3-7 items per session, fitting the time.`,
        `Each item: kind "sets" (sets, reps, weight in ${input.weightUnit} or null for bodyweight), "timed" (sets, seconds per set; e.g. plank 3 sets of 45 seconds, run 1 set of 1200 seconds) or "distance" (sets 1, distance, unit km or mi). Leave fields that don't apply null. eachSide true only for one-sided timed holds, else null. Conservative starting loads for the level; beginners mostly bodyweight or light.`,
        `move: the movement pattern that fits best: pushup, squat, lunge, plank, bench, overhead, hinge (deadlifts, swings), row, pullup (also pulldowns), curl, bridge, crunch, jumpingjack, burpee, climber, run, cycle, stretch, or generic if none fits.`,
      ]
  const blocks = [
    !input.flow &&
      `Start each session with a 3-5 minute warm-up: 1-3 timed items (e.g. easy jog, arm circles, bodyweight squats) with block "warmup".`,
    (input.flow || input.run) &&
      `End each session with a 3-5 minute cool-down: 1-3 gentle timed items with block "cooldown".`,
    `Every other item has block "main".`,
    input.flow
      ? `rest: seconds between sets; 0 for poses in a flow.`
      : `rest: seconds between sets: 90-180 for heavy compound lifts, 60-90 for other weighted work, 30-60 for bodyweight, 0 for warm-up, cool-down and single-set items.`,
  ]
  return [
    ...head,
    ...rules,
    ...blocks.filter((x): x is string => !!x),
    `cue: one short form cue (max 12 words), practical, no medical claims.`,
    `Write names and cues in ${input.language}.`,
  ]
    .filter(Boolean)
    .join('\n')
}

// Coach lines around a session. Prompts for the background model, plus template
// fallbacks in the six app languages for when it fails.
const LANG_NAMES: Record<string, string> = {
  en: 'English',
  ja: 'Japanese',
  es: 'Spanish',
  pt: 'Portuguese',
  fr: 'French',
  zh: 'Simplified Chinese',
}
export const langName = (lang: string) => LANG_NAMES[lang.slice(0, 2)] ?? 'English'

const clean1 = (s: string, max = 220) =>
  s
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^["“”']+|["“”']+$/g, '')
    .trim()
    .slice(0, max)

export function pepPrompt(i: {
  coach: string
  sessionName: string
  context: string
  lang: string
}) {
  return [
    `You are ${i.coach}, a warm, direct habit coach. The user is about to start their workout "${i.sessionName}".`,
    i.context
      ? `Facts (the only things you know about their training): ${i.context}.`
      : 'You know nothing else about their training.',
    `Write ONE short pep-talk line (max 20 words) in ${langName(i.lang)}. You may refer to one of the facts above; never invent numbers, PRs or past sessions that aren't listed. No emojis, no hashtags, no quotes, no medical advice.`,
  ]
    .filter(Boolean)
    .join('\n')
}

const PEP: Record<string, (name: string) => string> = {
  en: (n) => `${n || 'Today'}: one set at a time. Let's go.`,
  ja: (n) => `${n || '今日のセッション'}、1セットずつ。いきましょう。`,
  es: (n) => `${n || 'Hoy'}: serie a serie. ¡Vamos!`,
  pt: (n) => `${n || 'Hoje'}: uma série de cada vez. Vamos lá!`,
  fr: (n) => `${n || "Aujourd'hui"} : une série à la fois. On y va !`,
  zh: (n) => `${n || '今天'}：一组一组来。开始吧！`,
}
export const pepFallback = (lang: string, sessionName: string) =>
  (PEP[lang.slice(0, 2)] ?? PEP.en)(sessionName)

export type DebriefFacts = { sessionName: string; prs: string[]; skipped: string[]; next?: string }

export function debriefPrompt(
  i: DebriefFacts & { coach: string; lang: string; done: number; total: number },
) {
  return [
    `You are ${i.coach}, a warm, direct habit coach. The user just finished their workout "${i.sessionName}": ${i.done}/${i.total} exercises done.`,
    i.prs.length ? `New personal bests: ${i.prs.join(', ')}.` : '',
    i.skipped.length ? `Skipped: ${i.skipped.join(', ')}.` : '',
    i.next ? `Next session in the rotation: ${i.next}.` : '',
    `Write a short debrief (max 2 sentences, 35 words) in ${langName(i.lang)}: celebrate the PRs if any, name what was skipped kindly if anything, say what's next. Use only the facts above; never invent numbers. No emojis, no quotes, no medical advice.`,
  ]
    .filter(Boolean)
    .join('\n')
}

const DEBRIEF: Record<string, (f: DebriefFacts) => string[]> = {
  en: (f) => [
    `${f.sessionName || 'Session'} done.`,
    f.prs.length ? `New best: ${f.prs.join(', ')}.` : '',
    f.skipped.length ? `Skipped ${f.skipped.join(', ')}; next time.` : '',
    f.next ? `Next up: ${f.next}.` : '',
  ],
  ja: (f) => [
    `${f.sessionName || 'セッション'}完了。`,
    f.prs.length ? `自己ベスト：${f.prs.join('、')}。` : '',
    f.skipped.length ? `${f.skipped.join('、')}は次回に。` : '',
    f.next ? `次は${f.next}。` : '',
  ],
  es: (f) => [
    `${f.sessionName || 'Sesión'} hecha.`,
    f.prs.length ? `Nuevo récord: ${f.prs.join(', ')}.` : '',
    f.skipped.length ? `Te saltaste ${f.skipped.join(', ')}; la próxima.` : '',
    f.next ? `Siguiente: ${f.next}.` : '',
  ],
  pt: (f) => [
    `${f.sessionName || 'Sessão'} feita.`,
    f.prs.length ? `Novo recorde: ${f.prs.join(', ')}.` : '',
    f.skipped.length ? `Ficou de fora: ${f.skipped.join(', ')}; na próxima.` : '',
    f.next ? `A seguir: ${f.next}.` : '',
  ],
  fr: (f) => [
    `${f.sessionName || 'Séance'} terminée.`,
    f.prs.length ? `Nouveau record : ${f.prs.join(', ')}.` : '',
    f.skipped.length ? `${f.skipped.join(', ')} sauté ; la prochaine fois.` : '',
    f.next ? `Prochaine : ${f.next}.` : '',
  ],
  zh: (f) => [
    `${f.sessionName || '训练'}完成。`,
    f.prs.length ? `新纪录：${f.prs.join('、')}。` : '',
    f.skipped.length ? `跳过了${f.skipped.join('、')}，下次再练。` : '',
    f.next ? `下一次：${f.next}。` : '',
  ],
}
export const debriefFallback = (lang: string, f: DebriefFacts) =>
  (DEBRIEF[lang.slice(0, 2)] ?? DEBRIEF.en)(f)
    .filter(Boolean)
    .join(lang.startsWith('ja') || lang.startsWith('zh') ? '' : ' ')

export const coachLineOut = (raw: string, fallback: string) => clean1(raw) || fallback

export type WorkoutStats = {
  // Oldest first: Monday of each week and logged sessions in it.
  perWeek: { week: string; n: number }[]
  exercises: {
    itemId: string
    name: string
    field: 'weight' | 'reps' | 'seconds' | 'distance'
    unit?: string
    // Oldest first: the best set per session.
    points: { date: string; value: number }[]
  }[]
  prs: { date: string; text: string }[]
  total: number
}

const mondayOf = (d: string) => {
  const n = dayNum(d)
  // 1970-01-01 was a Thursday.
  const monday = n - ((n + 3) % 7)
  return new Date(monday * 86_400_000).toISOString().slice(0, 10)
}

// The Workouts section of a habit's stats: sessions per week (12 weeks), each
// exercise's best set over time, and PRs. Newest first in; unlogged ticks skipped.
export function workoutStats(
  done: readonly { date: string; items: ItemLog[]; prs?: string[]; unlogged?: boolean }[],
  today: string,
  weeks = 12,
): WorkoutStats | null {
  const logged = done.filter((d) => !d.unlogged && d.items.some((l) => l.sets.some((x) => x.done)))
  if (!logged.length) return null
  const thisWeek = dayNum(mondayOf(today))
  const perWeek = Array.from({ length: weeks }, (_, i) => ({
    week: new Date((thisWeek - (weeks - 1 - i) * 7) * 86_400_000).toISOString().slice(0, 10),
    n: 0,
  }))
  for (const d of logged) {
    const w = perWeek.find((x) => x.week === mondayOf(d.date))
    if (w) w.n++
  }
  const byItem = new Map<string, WorkoutStats['exercises'][number]>()
  for (const d of [...logged].reverse())
    for (const l of d.items) {
      const sets = l.sets.filter((x) => x.done)
      if (!sets.length) continue
      const field: WorkoutStats['exercises'][number]['field'] =
        l.kind === 'timed'
          ? 'seconds'
          : l.kind === 'distance'
            ? 'distance'
            : sets.some((x) => x.weight)
              ? 'weight'
              : 'reps'
      const value = Math.max(...sets.map((x) => x[field] ?? 0))
      if (!value) continue
      const e = byItem.get(l.itemId) ?? {
        itemId: l.itemId,
        name: l.name,
        field,
        unit: l.unit,
        points: [],
      }
      if (e.field !== field) continue
      e.name = l.name
      e.points.push({ date: d.date, value })
      byItem.set(l.itemId, e)
    }
  const exercises = [...byItem.values()].sort((a, b) => b.points.length - a.points.length)
  const prs = logged
    .flatMap((d) => (d.prs ?? []).map((text) => ({ date: d.date, text })))
    .slice(0, 10)
  return { perWeek, exercises, prs, total: logged.length }
}
